import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Users, BarChart3, Images, Star, FileText, Building2, Phone, MessageCircle, ExternalLink, Briefcase, CalendarClock, LayoutDashboard, Siren, Sparkles, Bell, Wallet, Tag, UserCog, Receipt, ClipboardCheck, Gift, Plus, ListTodo } from "lucide-react";
import { api, adminHeaders, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import AdminJobs from "@/admin/AdminJobs";
import AdminScheduling from "@/admin/AdminScheduling";
import AdminOperations from "@/admin/AdminOperations";
import AdminQuotes from "@/admin/AdminQuotes";
import AdminFinance from "@/admin/finance/AdminFinance";
import AdminPricing from "@/admin/AdminPricing";
import AdminClients from "@/admin/AdminClients";
import AdminInvoices from "@/admin/AdminInvoices";
import AssessmentWorkbench from "@/admin/AssessmentWorkbench";
import NotificationBell from "@/admin/NotificationBell";
import AdminNotifications from "@/admin/AdminNotifications";
import AdminRewards from "@/admin/AdminRewards";
import AdminQuickAdd from "@/admin/AdminQuickAdd";
import AdminTasks from "@/admin/AdminTasks";
import CommandCenter from "@/admin/CommandCenter";
import { registerServiceWorker } from "@/lib/push";

const STATUS_COLORS = {
  "New Lead": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Waiting for Information": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Video Received": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  "Quote in Preparation": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  "Quote Sent": "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "Follow-Up Needed": "bg-orange-500/15 text-orange-300 border-orange-500/30",
  "Confirmed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Completed": "bg-green-600/15 text-green-300 border-green-600/30",
  "Lost": "bg-red-500/15 text-red-300 border-red-500/30",
};

function LeadDrawer({ lead, statuses, onClose, onUpdated }) {
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(lead.status);
  const isRescue = lead.source === "rescue" || (lead.notes || "").includes("ASCENDIA RESCUE");
  const ac = lead.ai_collected || null;
  const est = ac?.estimate || null;
  const cur = est?.currency || "\u20aa";
  const sourceLabel = {
    "property-intelligence": "Property Intelligence",
    "estimate": "Instant Estimate",
    "ask-ascendia": "Ask Ascendia",
    "rescue": "Ascendia Rescue",
    "website": "Website",
    "partner": "Partner",
  }[lead.source] || lead.source || "Website";
  const save = async () => {
    try { await api.patch(`/admin/leads/${lead.id}/status`, { status, note: note || null }, { headers: adminHeaders() }); toast.success("Lead updated"); onUpdated(); onClose(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto bg-[#101011] border-l border-[color:var(--border-subtle)] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            {isRescue && (
              <span data-testid="lead-urgent-rescue-badge" className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-300">
                <Siren className="h-3.5 w-3.5" /> Urgent Rescue
              </span>
            )}
            <h3 className="font-display text-xl font-semibold text-white">{lead.full_name}</h3>
            <p className="text-xs font-mono text-gold-soft">{lead.ref}</p>
          </div>
          <button onClick={onClose} className="text-muted-c hover:text-white">✕</button>
        </div>
        <div className="mt-4 flex gap-2">
          <a href={`tel:${lead.phone}`} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-[color:var(--border-subtle)] py-2 text-sm text-white hover:bg-white/5"><Phone className="h-4 w-4" /> Call</a>
          <a href={`https://wa.me/${(lead.phone||'').replace(/\D/g,'').replace(/^0/,'972')}`} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-[color:var(--border-subtle)] py-2 text-sm text-white hover:bg-white/5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
        </div>
        <div className="mt-5 space-y-2 text-sm">
          {[["Source", sourceLabel], ["Phone", lead.phone], ["Email", lead.email], ["Service", lead.service_required], ["City", lead.city], ["Address", lead.address], ["Size", lead.size_sqm && `${lead.size_sqm} sqm`], ["Rooms", lead.rooms], ["Bathrooms", lead.bathrooms], ["State", lead.property_state], ["Furnishing", lead.furnishing], ["Desired date", lead.desired_date], ["Deadline", lead.deadline], ["Priority", lead.priority_areas], ["Budget", lead.budget_range], ["Contact via", lead.preferred_contact], ["Language", lead.preferred_language]].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-[color:var(--border-subtle)]/40 py-1.5"><span className="text-muted-c">{k}</span><span className="text-white text-right">{v}</span></div>
          ))}
        </div>
        {lead.notes && <div className="mt-4"><p className="text-xs uppercase tracking-wide text-muted-c">Client notes</p><p className="text-sm text-secondary mt-1 whitespace-pre-line">{lead.notes}</p></div>}

        {ac && (
          <div className="mt-4 rounded-xl border border-[color:var(--border-subtle)] bg-gold/[0.03] p-4" data-testid="lead-property-plan-panel">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gold-soft"><Sparkles className="h-3.5 w-3.5" /> Property Plan</p>
            {est && (
              <div className="mt-3 flex items-baseline justify-between">
                <span className="text-xs text-muted-c">Customer's guide estimate</span>
                <span className="font-display text-lg font-semibold text-white" data-testid="lead-estimate-range">{cur}{Number(est.low).toLocaleString()} – {cur}{Number(est.high).toLocaleString()}</span>
              </div>
            )}
            <div className="mt-3 space-y-1.5 text-sm">
              {[
                ["Recommended service", lead.service_required],
                ["Complexity", ac.complexity ? `${ac.complexity.level}${ac.complexity.factors?.length ? ` — ${ac.complexity.factors.join(", ")}` : ""}` : null],
                ["Est. duration", ac.duration ? `${ac.duration.low}–${ac.duration.high} hrs (${ac.duration.band})` : null],
                ["Add-ons", ac.addons?.length ? ac.addons.join(", ") : null],
                ["Photos provided", ac.photos ? `${ac.photos}` : null],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[color:var(--border-subtle)]/40 py-1"><span className="text-muted-c">{k}</span><span className="text-white text-right">{v}</span></div>
              ))}
            </div>
            {ac.plan_token && (
              <a href={`/property-intelligence?plan=${ac.plan_token}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold-soft hover:text-gold">
                Open customer's saved plan <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <p className="mt-3 text-[11px] text-muted-c">Customer-entered estimate — confirm final scope & price before quoting.</p>
          </div>
        )}
        {lead.media?.length > 0 && (
          <div className="mt-4"><p className="text-xs uppercase tracking-wide text-muted-c mb-2">Media ({lead.media.length})</p>
            <div className="grid grid-cols-3 gap-2">{lead.media.map((m, i) => (
              <a key={m.url || m.path || `media-${i}`} href={mediaUrl(m.url)} target="_blank" rel="noopener noreferrer" className="relative aspect-square rounded-lg overflow-hidden border border-[color:var(--border-subtle)] bg-black/40 flex items-center justify-center">
                {m.content_type?.startsWith("image") ? <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" /> : <FileText className="h-6 w-6 text-gold-soft" />}
                <ExternalLink className="absolute bottom-1 right-1 h-3 w-3 text-white/70" />
              </a>
            ))}</div>
          </div>
        )}
        {lead.notes_internal?.length > 0 && (
          <div className="mt-4"><p className="text-xs uppercase tracking-wide text-muted-c mb-1">Internal notes</p>{lead.notes_internal.map((n, i) => <p key={n.at || n.text || `note-${i}`} className="text-sm text-secondary border-l-2 border-gold/40 pl-2 mt-1">{n.text}</p>)}</div>
        )}
        <div className="mt-6 space-y-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white">{statuses.map((s) => <option key={s}>{s}</option>)}</select>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note (optional)" className="w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35" />
          <button onClick={save} className="w-full rounded-lg bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Save changes</button>
        </div>
      </div>
    </div>
  );
}

export function StatsOverview({ stats }) {
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total leads", stats.total_leads], ["Converted", stats.converted], ["New leads", stats.by_status?.["New Lead"] || 0], ["Quote sent", stats.by_status?.["Quote Sent"] || 0]].map(([k, v]) => (
          <div key={k} className="ascendia-card p-5"><p className="text-xs uppercase tracking-wide text-muted-c">{k}</p><p className="mt-2 font-display text-3xl font-semibold text-white">{v}</p></div>
        ))}
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="ascendia-card p-6"><h3 className="font-display font-semibold text-white mb-4">Top cities</h3>{(stats.top_cities||[]).map(([c, n]) => <div key={c} className="flex justify-between py-1.5 border-b border-[color:var(--border-subtle)]/40 text-sm"><span className="text-secondary">{c}</span><span className="text-gold-soft">{n}</span></div>)}{!stats.top_cities?.length && <p className="text-sm text-muted-c">No data yet.</p>}</div>
        <div className="ascendia-card p-6"><h3 className="font-display font-semibold text-white mb-4">Top services</h3>{(stats.top_services||[]).map(([c, n]) => <div key={c} className="flex justify-between py-1.5 border-b border-[color:var(--border-subtle)]/40 text-sm"><span className="text-secondary">{c}</span><span className="text-gold-soft">{n}</span></div>)}{!stats.top_services?.length && <p className="text-sm text-muted-c">No data yet.</p>}</div>
      </div>
    </div>
  );
}

export function LeadsBoard({ statuses, filter, setFilter, stats, shown, onSelect }) {
  return (
    <div data-testid="leads-pipeline-board">
      <div className="flex gap-2 flex-wrap mb-5">
        {["All", ...statuses].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs border ${filter === s ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>{s}{s !== "All" && stats?.by_status?.[s] ? ` (${stats.by_status[s]})` : ""}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((l) => (
          <button key={l.id} onClick={() => onSelect(l)} data-testid="lead-card" className="ascendia-card p-5 text-left">
            <div className="flex justify-between items-start"><span className="font-semibold text-white">{l.full_name}</span><span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[l.status] || "border-white/20 text-white/70"}`}>{l.status}</span></div>
            <p className="text-xs font-mono text-gold-soft mt-1">{l.ref}</p>
            <p className="mt-2 text-sm text-secondary">{l.service_required || "—"}</p>
            <p className="text-xs text-muted-c mt-1">{l.city || "—"} · {l.media?.length || 0} media</p>
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-muted-c text-sm mt-6">No leads in this view yet.</p>}
    </div>
  );
}

export function PartnersList({ partners }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {partners.map((p) => (
        <div key={p.id} className="ascendia-card p-5">
          <p className="font-semibold text-white">{p.company}</p>
          <p className="text-sm text-secondary">{p.contact_name} · {p.business_type}</p>
          <p className="text-sm text-muted-c mt-1">{p.phone}{p.email ? ` · ${p.email}` : ""}</p>
          {p.message && <p className="text-sm text-secondary mt-2">{p.message}</p>}
        </div>
      ))}
      {partners.length === 0 && <p className="text-muted-c text-sm">No partner inquiries yet.</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("operations");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [partners, setPartners] = useState([]);
  const [me, setMe] = useState(null);
  const [pendingQuoteId, setPendingQuoteId] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [ccKey, setCcKey] = useState(0);
  const pendingLeadRef = React.useRef(null);

  const load = () => {
    const h = { headers: adminHeaders() };
    api.get("/admin/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/admin/stats", h).then(({ data }) => setStats(data)).catch(() => nav(`/admin/login?next=${encodeURIComponent(location.pathname + location.search)}`));
    api.get("/admin/leads", h).then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/partner-inquiries", h).then(({ data }) => setPartners(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    registerServiceWorker();
    // Deep-link from a push notification: /admin?lead= | ?quote= | ?tab=
    const q = new URLSearchParams(location.search);
    const leadId = q.get("lead"); const quoteId = q.get("quote"); const t = q.get("tab");
    if (leadId) { setTab("leads"); pendingLeadRef.current = leadId; }
    else if (quoteId) { setTab("quotes"); }
    else if (t) { setTab(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the exact lead drawer once leads have loaded (deep link).
  useEffect(() => {
    if (pendingLeadRef.current && leads.length) {
      const l = leads.find((x) => x.id === pendingLeadRef.current);
      if (l) { setActive(l); pendingLeadRef.current = null; }
    }
  }, [leads]);

  const handleOpenNotification = (url) => {
    try {
      const q = new URL(url, window.location.origin).searchParams;
      const leadId = q.get("lead"); const quoteId = q.get("quote"); const t = q.get("tab");
      if (leadId) { setTab("leads"); const l = leads.find((x) => x.id === leadId); if (l) setActive(l); else pendingLeadRef.current = leadId; }
      else if (quoteId) { setTab("quotes"); }
      else if (t) { setTab(t); }
    } catch (e) { console.error("Failed to route notification deep-link:", e); }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { console.error("Admin logout request failed:", e); }
    nav("/admin/login");
  };
  const statuses = stats?.statuses || [];
  const shown = useMemo(
    () => (filter === "All" ? leads : leads.filter((l) => l.status === filter)),
    [filter, leads]
  );

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white">
      <header className="border-b border-[color:var(--border-subtle)] sticky top-0 bg-[#0B0B0C]/95 backdrop-blur z-30">
        <div className="container-ascendia flex h-16 items-center justify-between">
          <div className="font-display font-semibold">Ascendia <span className="text-gold">Admin</span></div>
          <div className="flex items-center gap-3">
            <button onClick={() => setQuickAddOpen(true)} data-testid="quick-add-button" className="inline-flex items-center gap-2 rounded-lg bg-gold px-3.5 py-2 text-sm font-semibold text-black hover:bg-gold-soft transition-colors"><Plus className="h-4 w-4" /> Quick add</button>
            <NotificationBell onOpen={handleOpenNotification} />
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="container-ascendia py-8">
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-nav">
          {[["operations", "Today", LayoutDashboard], ["tasks", "Tasks", ListTodo], ["overview", "Overview", BarChart3], ["leads", "Leads", Users], ["clients", "Clients", UserCog], ["quotes", "Quotes", FileText], ["jobs", "Jobs", Briefcase], ["scheduling", "Scheduling", CalendarClock], ["assessment", "Assessment", ClipboardCheck], ["invoices", "Invoices", Receipt], ...(me?.is_owner ? [["finance", "Finance", Wallet], ["pricing", "Pricing", Tag], ["rewards", "Rewards", Gift]] : []), ["partners", "Partners", Building2], ["notifications", "Notifications", Bell]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm border transition-colors ${tab === k ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5"}`}><Icon className="h-4 w-4" /> {label}</button>
          ))}
        </div>

        {tab === "operations" && (
          <div className="space-y-10">
            <CommandCenter key={ccKey} onNav={setTab} />
            <AdminOperations onOpenJob={() => setTab("jobs")} />
          </div>
        )}

        {tab === "tasks" && <AdminTasks key={ccKey} />}

        {tab === "overview" && stats && <StatsOverview stats={stats} />}

        {tab === "leads" && (
          <LeadsBoard statuses={statuses} filter={filter} setFilter={setFilter} stats={stats} shown={shown} onSelect={setActive} />
        )}

        {tab === "quotes" && <AdminQuotes initialQuoteId={pendingQuoteId} onConsumeInitial={() => setPendingQuoteId(null)} />}

        {tab === "jobs" && <AdminJobs />}

        {tab === "scheduling" && <AdminScheduling />}

        {tab === "notifications" && <AdminNotifications isOwner={me?.is_owner} />}

        {tab === "finance" && me?.is_owner && <AdminFinance />}

        {tab === "pricing" && me?.is_owner && <AdminPricing />}
        {tab === "rewards" && me?.is_owner && <AdminRewards />}

        {tab === "partners" && <PartnersList partners={partners} />}

        {tab === "clients" && <AdminClients />}

        {tab === "invoices" && <AdminInvoices />}

        {tab === "assessment" && <AssessmentWorkbench onOpenQuote={(qid) => { setPendingQuoteId(qid); setTab("quotes"); }} />}
      </div>
      {active && <LeadDrawer lead={active} statuses={statuses} onClose={() => setActive(null)} onUpdated={load} />}
      <AdminQuickAdd
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        isOwner={me?.is_owner}
        onCreated={(type) => {
          setCcKey((k) => k + 1);
          if (type === "lead") { load(); }
        }}
      />
    </div>
  );
}
